import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import {
  buildSpread,
  type DrawnCard,
  type Locale,
  type Reading,
  type Spread,
} from 'shared';
import { api, apiFetch, ApiError } from '@/lib/api';
import { useAuthStore } from './auth';
import { useContentStore } from './content';
import { usePrefsStore } from './prefs';

export type ReadingPhase = 'idle' | 'drawing' | 'ready' | 'error';

/** The Oracle, asked on request: a pause for effect, then the reply streaming in. */
export type OraclePhase = 'idle' | 'interpreting' | 'streaming' | 'error';

export interface OracleReply {
  /** How many cards were face up when it was asked. */
  revealedCount: number;
  context: string | null;
  text: string;
}

/** What the reader may type, in the question and the follow-up context. */
export const READER_TEXT_MAX = 255;

/**
 * The sparkle runs at least this long, even when the first words are back
 * sooner: an answer that lands instantly reads as canned.
 */
const INTERPRET_MIN_MS = 900;

/**
 * Text that arrived during the sparkle is typed out rather than dropped in
 * as one block: a tick every DRAIN_TICK_MS, each taking a share of what is
 * waiting, so a backlog clears in about DRAIN_TICKS ticks and then keeps
 * pace with the stream.
 */
const DRAIN_TICK_MS = 30;
const DRAIN_TICKS = 14;
const DRAIN_MIN_CHARS = 3;

/** Local calendar day, `YYYY-MM-DD`, so "per day" matches the reader's own day. */
const dayOf = (date: Date): string => {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/**
 * Drawing and reading a spread.
 *
 * Paid draws, and the free tier's one daily reading, are dealt by the server —
 * a client-side shuffle would let someone re-roll until they liked the cards,
 * and every synthesis is billed. When the server can't be reached the free
 * draw falls back to happening here, in the browser, with no interpretation,
 * so it still works with no connection; that fallback is shown but not kept.
 *
 * The server is the only record of a reader's history. Nothing is written to
 * the device, so a browser shared between accounts never mixes them.
 */
export const useReadingStore = defineStore('reading', () => {
  const phase = ref<ReadingPhase>('idle');
  const cards = ref<DrawnCard[]>([]);
  const question = ref<string | null>(null);
  const spread = ref<Spread>(3);
  const deckSlug = ref('rws');
  const readingId = ref<string | null>(null);
  const error = ref<string | null>(null);
  const history = ref<Reading[]>([]);
  /** Positions turned face up, in the order the reader turned them. */
  const revealed = ref<number[]>([]);
  const oraclePhase = ref<OraclePhase>('idle');
  const oracleReplies = ref<OracleReply[]>([]);
  const oracleError = ref<string | null>(null);
  /** Free tier only: today's one reading has already been drawn. */
  const dailyLimitReached = ref(false);
  /**
   * The day an offline fallback draw was dealt this session. The server never
   * saw it, so it is held here only to stop the fallback being re-rolled.
   */
  const fallbackDay = ref<string | null>(null);

  /** Cancels an Oracle reply in flight when the reading is put away. */
  let inFlight: AbortController | null = null;

  const auth = useAuthStore();
  const prefs = usePrefsStore();
  const content = useContentStore();

  /** Every card that can be turned (a locked card never is). */
  const turnable = computed(() => cards.value.filter((c) => !c.locked));
  const allRevealed = computed(
    () => turnable.value.length > 0 && turnable.value.every((c) => revealed.value.includes(c.position)),
  );
  const lastAskedCount = computed(() => oracleReplies.value.at(-1)?.revealedCount ?? 0);
  /** A new question is worth asking only once another card is face up. */
  const canAsk = computed(
    () =>
      !!readingId.value &&
      (oraclePhase.value === 'idle' || oraclePhase.value === 'error') &&
      revealed.value.length > lastAskedCount.value,
  );

  function reset(): void {
    inFlight?.abort();
    inFlight = null;
    phase.value = 'idle';
    cards.value = [];
    readingId.value = null;
    error.value = null;
    revealed.value = [];
    oraclePhase.value = 'idle';
    oracleReplies.value = [];
    oracleError.value = null;
  }

  function reveal(position: number): void {
    const card = cards.value.find((c) => c.position === position);
    if (!card || card.locked || revealed.value.includes(position)) return;
    revealed.value = [...revealed.value, position];
  }

  // Another account signed in (or this one signed out): nothing of the
  // previous reader's may stay on screen.
  watch(
    () => auth.user?.id ?? null,
    () => {
      reset();
      question.value = null;
      history.value = [];
      dailyLimitReached.value = false;
      fallbackDay.value = null;
    },
  );

  /** An offline draw with no AI, using the SPEC's alternating reversal pattern. */
  async function drawFree(size: Spread, deck: string): Promise<void> {
    reset();

    if (fallbackDay.value === dayOf(new Date())) {
      dailyLimitReached.value = true;
      return;
    }

    phase.value = 'drawing';
    spread.value = size;
    deckSlug.value = deck;

    // Same numbering as the server (readings so far, plus one), so the
    // alternation carries on from the reader's real history.
    const readingNumber = history.value.length + 1;

    cards.value = buildSpread({
      tier: auth.tier,
      spread: size,
      pool: content.allCardIds,
      prefs: {
        reversalsEnabled: prefs.reversalsEffective,
        reversalFrequency: prefs.frequencyEffective,
      },
      readingNumber,
    });

    fallbackDay.value = dayOf(new Date());
    dailyLimitReached.value = true;
    phase.value = 'ready';
  }

  /**
   * A server-dealt draw: paid, or the free tier's daily one. The cards come
   * back face down; the Oracle is only asked when the reader chooses to.
   */
  async function drawOnServer(
    size: Spread,
    deck: string,
    theme: string | null,
    locale: Locale,
    daily = false,
  ): Promise<void> {
    reset();
    phase.value = 'drawing';
    spread.value = size;
    deckSlug.value = deck;
    question.value = theme;

    let reading: Reading;
    try {
      reading = await api<Reading>(daily ? '/readings/daily' : '/readings', {
        method: 'POST',
        body: daily
          ? { locale, tz: Intl.DateTimeFormat().resolvedOptions().timeZone }
          : { deckSlug: deck, spread: size, question: theme, locale },
      });
    } catch (cause) {
      const apiError = cause instanceof ApiError ? cause : null;
      if (daily && apiError?.code === 'daily_limit') {
        // The server's count is the real one.
        reset();
        dailyLimitReached.value = true;
        return;
      }
      // The free reading must still work offline or with the server down;
      // only the Oracle is lost.
      if (daily && (!apiError || apiError.code === 'offline' || apiError.status >= 500)) {
        return drawFree(size, deck);
      }
      phase.value = 'error';
      error.value = apiError?.message ?? 'The reading could not be started.';
      return;
    }

    cards.value = reading.cards;
    readingId.value = reading.id;
    phase.value = 'ready';
    if (daily) dailyLimitReached.value = true;

    // The server has already saved it; mirror it into the list on screen
    // rather than refetching the whole history.
    if (!history.value.some((r) => r.id === reading.id)) {
      history.value = [reading, ...history.value];
    }
  }

  async function draw(
    size: Spread,
    deck: string,
    theme: string | null,
    locale: Locale,
  ): Promise<void> {
    const cleaned = theme?.slice(0, READER_TEXT_MAX) ?? null;
    if (auth.entitlements.aiSynthesis) {
      await drawOnServer(size, deck, cleaned, locale);
    } else if (auth.entitlements.dailyOracle) {
      // The server enforces this too; checking first just saves a round trip.
      if (dailyLimitReached.value) {
        reset();
        dailyLimitReached.value = true;
        return;
      }
      await drawOnServer(3, deck, null, locale, true);
    } else {
      await drawFree(size, deck);
    }
  }

  /**
   * Asks the Oracle about the cards face up right now. The reply streams over
   * SSE (EventSource cannot POST, so the stream is read by hand), held back
   * until the interpreting animation has had its moment.
   */
  async function askOracle(context: string, locale: Locale): Promise<void> {
    if (!canAsk.value || !readingId.value) return;

    const id = readingId.value;
    const turned = [...revealed.value];
    const extra = context.trim().slice(0, READER_TEXT_MAX) || null;
    const controller = new AbortController();
    inFlight = controller;

    oraclePhase.value = 'interpreting';
    oracleError.value = null;

    // Deltas queue up while the sparkle plays, then drain onto the page at
    // a steady pace (see DRAIN_TICKS) instead of landing all at once.
    let pending = '';
    let shown = false;
    let streamEnded = false;
    let drainTimer: ReturnType<typeof setInterval> | null = null;
    let drained: () => void = () => {};
    const drainDone = new Promise<void>((resolve) => (drained = resolve));

    const write = (text: string): void => {
      if (!shown) {
        shown = true;
        oracleReplies.value = [
          ...oracleReplies.value,
          { revealedCount: turned.length, context: extra, text: '' },
        ];
        oraclePhase.value = 'streaming';
      }
      // The reactive copy, so each delta re-renders.
      oracleReplies.value.at(-1)!.text += text;
    };
    const stopDrain = (): void => {
      if (drainTimer) clearInterval(drainTimer);
      drainTimer = null;
      drained();
    };
    const tick = (): void => {
      if (controller.signal.aborted) return stopDrain();
      if (!pending) {
        if (streamEnded) stopDrain();
        return;
      }
      const take = Math.max(DRAIN_MIN_CHARS, Math.ceil(pending.length / DRAIN_TICKS));
      write(pending.slice(0, take));
      pending = pending.slice(take);
    };
    const append = (text: string): void => {
      if (!controller.signal.aborted) pending += text;
    };
    const minimum = new Promise<void>((resolve) => setTimeout(resolve, INTERPRET_MIN_MS)).then(() => {
      drainTimer = setInterval(tick, DRAIN_TICK_MS);
    });

    const fail = (message: string): void => {
      if (controller.signal.aborted) return;
      oracleError.value = message;
      oraclePhase.value = 'error';
    };

    let response: Response;
    try {
      response = await apiFetch(`/readings/${encodeURIComponent(id)}/oracle`, {
        method: 'POST',
        body: { revealed: turned, context: extra, locale },
        headers: { Accept: 'text/event-stream' },
        signal: controller.signal,
      });
    } catch (cause) {
      streamEnded = true;
      await minimum;
      stopDrain();
      return fail(cause instanceof ApiError ? cause.message : 'Could not reach the Oracle');
    }

    if (!response.ok || !response.body) {
      const body = (await response.json().catch(() => null)) as
        | { error?: string; code?: string }
        | null;
      streamEnded = true;
      await minimum;
      stopDrain();
      return fail(body?.error ?? 'The Oracle could not be asked right now.');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let completed = false;
    let failed = false;
    let synthesis: string | null = null;

    const handle = (event: string, data: string): void => {
      const payload = JSON.parse(data) as Record<string, unknown>;
      switch (event) {
        case 'delta':
          append(payload.text as string);
          break;
        case 'done':
          if (typeof payload.synthesis === 'string') synthesis = payload.synthesis;
          completed = true;
          break;
        case 'error':
          failed = true;
          oracleError.value =
            (payload.error as string) ?? 'The Oracle could not finish this reading.';
          break;
      }
    };

    try {
      // Frames are separated by a blank line.
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        let boundary = buffer.indexOf('\n\n');

        while (boundary !== -1) {
          const frame = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);

          let name = 'message';
          const dataLines: string[] = [];
          for (const line of frame.split('\n')) {
            if (line.startsWith('event: ')) name = line.slice(7).trim();
            else if (line.startsWith('data: ')) dataLines.push(line.slice(6));
          }
          if (dataLines.length) {
            try {
              handle(name, dataLines.join('\n'));
            } catch {
              // A malformed frame should not take down a reply in progress.
            }
          }

          boundary = buffer.indexOf('\n\n');
        }
      }
    } catch {
      // Aborted (the reading was put away) or the connection dropped.
    }

    streamEnded = true;
    await minimum;
    if (!completed || failed) stopDrain();
    await drainDone;
    if (controller.signal.aborted) return;
    inFlight = null;

    if (!completed || failed) {
      // The server only records a reply that finished, so the turn is not
      // spent: a partial reply is taken down and the reader can ask again.
      if (shown) oracleReplies.value = oracleReplies.value.slice(0, -1);
      return fail(oracleError.value ?? 'The Oracle was interrupted. Try asking again.');
    }

    oraclePhase.value = 'idle';
    const saved = history.value.find((r) => r.id === id);
    if (saved && synthesis) saved.synthesis = synthesis;
  }

  /**
   * Free tier: has today's reading been drawn? Mirrors the server's rule — the
   * draw itself counts — using the loaded history.
   */
  async function refreshDailyLimit(): Promise<void> {
    if (auth.entitlements.aiSynthesis) {
      dailyLimitReached.value = false;
      return;
    }
    const today = dayOf(new Date());
    dailyLimitReached.value =
      fallbackDay.value === today ||
      history.value.some((r) => dayOf(new Date(r.createdAt)) === today);
  }

  async function loadHistory(): Promise<void> {
    if (!auth.isSignedIn) {
      history.value = [];
      return;
    }

    const userId = auth.user?.id;
    try {
      const remote = await api<{ readings: Reading[] }>('/readings', { query: { limit: 100 } });
      // Signed out or switched account mid-flight: this list is not theirs.
      if (auth.user?.id !== userId) return;
      history.value = remote.readings;
    } catch {
      // Offline. Whatever was loaded earlier this session stands.
    }
  }

  return {
    phase, cards, question, spread, deckSlug, readingId, error, history, dailyLimitReached,
    revealed, oraclePhase, oracleReplies, oracleError, turnable, allRevealed, lastAskedCount, canAsk,
    reset, reveal, draw, drawFree, askOracle, loadHistory, refreshDailyLimit,
  };
});
